import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button } from '../ui/Button';
export interface TourStep {
  targetId: string; // data-tour attribute value
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}
interface GuidedTourProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  isRTL: boolean;
  currentStepIndex: number;
  onStepChange: (index: number) => void;
}
export function GuidedTour({
  steps,
  isOpen,
  onClose,
  onComplete,
  isRTL,
  currentStepIndex,
  onStepChange
}: GuidedTourProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0
  });
  const currentStep = steps[currentStepIndex];
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const element = document.querySelector(`[data-tour="${currentStep.targetId}"]`);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        // Calculate position based on preferred position or auto
        // Default to right for sidebar items (if LTR), left if RTL
        // Default to bottom for top items
        let top = rect.top + rect.height / 2;
        let left = rect.right + 20;
        if (isRTL) {
          left = rect.left - 340; // Approx width of card + padding
        }
        // Adjust if off screen
        if (left + 320 > window.innerWidth) {
          left = rect.left - 340;
        }
        if (left < 0) {
          left = rect.right + 20;
        }
        // Specific overrides based on step hint (if we added position to step)
        if (currentStep.position === 'bottom') {
          top = rect.bottom + 20;
          left = rect.left + rect.width / 2 - 160; // Center horizontally
        } else if (currentStep.position === 'top') {
          top = rect.top - 200; // Approx height
          left = rect.left + rect.width / 2 - 160;
        }
        setPosition({
          top,
          left
        });
        // Scroll into view if needed
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };
    // Small delay to allow layout changes (tab switching)
    const timer = setTimeout(updatePosition, 300);
    window.addEventListener('resize', updatePosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
    };
  }, [currentStepIndex, isOpen, isRTL, currentStep]);
  if (!isOpen || !currentStep) return null;
  const isLastStep = currentStepIndex === steps.length - 1;
  return <AnimatePresence data-id="element-195">
      {isOpen && <div className="fixed inset-0 z-[100] overflow-hidden" data-id="element-196">
          {/* Backdrop with hole or just overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} data-id="element-197" />

          {/* Spotlight Effect (Optional - just highlighting the target) */}
          {targetRect && <motion.div layoutId="tour-spotlight" className="absolute bg-transparent border-2 border-white/50 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] rounded-lg pointer-events-none transition-all duration-300 ease-in-out" style={{
        top: targetRect.top - 4,
        left: targetRect.left - 4,
        width: targetRect.width + 8,
        height: targetRect.height + 8,
        zIndex: 101
      }} data-id="element-198" />}

          {/* Tooltip Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} key={currentStepIndex} transition={{
        type: 'spring',
        duration: 0.5
      }} className="absolute z-[102] w-80 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100" style={{
        top: position.top,
        left: position.left,
        transform: 'translateY(-50%)' // Center vertically relative to target center
      }} data-id="element-199">
            {/* Arrow */}
            {/* Simplified arrow positioning logic could be added here */}

            <div className="flex justify-between items-start mb-4" data-id="element-200">
              <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-full" data-id="element-201">
                {currentStepIndex + 1} / {steps.length}
              </span>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-202">
                <X className="w-4 h-4" data-id="element-203" />
              </button>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-204">
              {isRTL ? currentStep.titleAr : currentStep.title}
            </h3>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed" data-id="element-205">
              {isRTL ? currentStep.descriptionAr : currentStep.description}
            </p>

            <div className="flex items-center justify-between gap-3" data-id="element-206">
              <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-700" data-id="element-207">
                {isRTL ? 'تخطي' : 'Skip'}
              </button>

              <Button onClick={() => {
            if (isLastStep) {
              onComplete();
            } else {
              onStepChange(currentStepIndex + 1);
            }
          }} className="bg-[#4f1964] hover:bg-[#3d1350] text-white px-6" size="sm" data-id="element-208">
                {isLastStep ? isRTL ? 'ابدأ الآن' : 'Start Now' : isRTL ? 'فهمت' : 'Got it'}
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}